import { useCallback, useEffect, useState } from "react";
import { useGetCounts } from "./useGetCounts";
import { withOutstandingVirtualTransfersCount } from "../../../util/badgeCount";

interface StatusCount {
	status: string;
	count: number;
}

interface FailureCount {
	count: number;
	failureReason: string;
}

export type BadgeCount = {
	creditRequests: StatusCount[];
	receives: StatusCount[];
	receiveFailures: FailureCount[];
	transfers: StatusCount[];
	transferFailures: FailureCount[];
	ediOutstanding: number;
};
export const useBadgeCount = (locationId: number) => {
	const [badgeCount, setBadgeCount] = useState<BadgeCount>({
		creditRequests: [],
		receives: [],
		receiveFailures: [],
		transfers: [],
		transferFailures: [],
		ediOutstanding: 0,
	});
	const [{ data: statusAndFailureCounts }, refreshCounts] = useGetCounts(locationId, true);

	const getBadgeCounts = useCallback(async () => {
		if (!locationId) return;

		await refreshCounts();
	}, [refreshCounts, locationId]);

	// Calculate badge counts when location changes, refreshing every 60 seconds
	useEffect(() => {
		getBadgeCounts();

		const interval = setInterval(() => {
			getBadgeCounts();
		}, 60000);

		// Cleanup
		return () => {
			clearInterval(interval);
		};
	}, [locationId, getBadgeCounts]);

	useEffect(() => {
		let badge: BadgeCount = {
			creditRequests: [],
			receives: [],
			receiveFailures: [],
			transfers: [],
			transferFailures: [],
			ediOutstanding: 0,
		};
		if (!statusAndFailureCounts) return;
		// Credit Requests
		badge.creditRequests =
			statusAndFailureCounts.find((count) => count.displayType === "CreditRequests")?.statusCount ?? [];
		// Receives
		const receivesCount = statusAndFailureCounts.find((count) => count.displayType === "PurchaseOrders");
		badge.receives = receivesCount?.statusCount ?? [];
		badge.receiveFailures = receivesCount?.failureCount ?? [];

		const transfersCount = statusAndFailureCounts.find((count) => count.displayType === "Transfers");
		const outstandingVirtualsCount =
			statusAndFailureCounts.find((count) => count.displayType === "OutstandingVirtuals")?.count ?? 0;
		badge.transfers = withOutstandingVirtualTransfersCount(
			transfersCount?.statusCount,
			outstandingVirtualsCount
		);
		badge.transferFailures = transfersCount?.failureCount ?? [];

		// EDI Outstanding Invoices
		badge.ediOutstanding = statusAndFailureCounts.find((count) => count.displayType === "EdiOutstandingInvoices")?.count ?? 0;

		setBadgeCount(badge);
	}, [statusAndFailureCounts, locationId]);

	return { badgeCount };
};
